/**
 * What makes a sphere-watch password strong enough, drawn as a checklist while
 * somebody types a new one.
 *
 * A COPY, and only a courtesy. The rules that refuse a password are the
 * server's (services/passwords.js), and its refusal names the rules that
 * failed in its own words. passwordRules.test.ts runs both over the same
 * passwords, so the two cannot drift apart without a red test.
 */
export const MIN_LENGTH = 12;
export const MAX_LENGTH = 128;

const COMMON = new Set([
  'password',
  'passwd',
  'pass',
  'qwerty',
  'qwertyuiop',
  'asdfgh',
  'asdfghjkl',
  'zxcvbn',
  'letmein',
  'welcome',
  'admin',
  'administrator',
  'changeme',
  'iloveyou',
  'monkey',
  'dragon',
  'sunshine',
  'football',
  'baseball',
  'abc',
  'abcdef',
  'secret',
  'master',
  'login',
  'limosphere',
  'spherewatch',
  'sphere',
  'watch',
  'dispatch',
  'dispatcher',
  'mydispatcher',
  'limo',
  'chauffeur',
]);

export interface PasswordRule {
  key: string;
  label: string;
  test: (password: string, context: { email?: string }) => boolean;
}

const letters = (value: string) => value.toLowerCase().replace(/[^\p{L}\p{Nd}]/gu, '');

export const RULES: PasswordRule[] = [
  { key: 'length', label: `At least ${MIN_LENGTH} characters`, test: (p) => [...p].length >= MIN_LENGTH },
  { key: 'max', label: `No more than ${MAX_LENGTH} characters`, test: (p) => p.length <= MAX_LENGTH },
  { key: 'lower', label: 'A lowercase letter', test: (p) => /\p{Ll}/u.test(p) },
  { key: 'upper', label: 'An uppercase letter', test: (p) => /\p{Lu}/u.test(p) },
  { key: 'digit', label: 'A number', test: (p) => /\p{Nd}/u.test(p) },
  { key: 'symbol', label: 'A symbol, such as ! # % or a space', test: (p) => /[^\p{L}\p{Nd}]/u.test(p) },
  { key: 'repeat', label: 'No character more than three times in a row', test: (p) => !/(.)\1{3,}/u.test(p) },
  {
    key: 'email',
    label: 'Not your email address or name',
    test: (p, { email }) => {
      const local = letters(String(email ?? '').split('@')[0]);
      return local.length < 3 || !letters(p).includes(local);
    },
  },
  {
    key: 'common',
    label: 'Not a common password with numbers or symbols added',
    test: (p) => !COMMON.has(p.toLowerCase().replace(/[^\p{L}]/gu, '')),
  },
];

/** Each rule, and whether `password` meets it. */
export const checklist = (password: string, email?: string) =>
  RULES.map((rule) => ({ key: rule.key, label: rule.label, met: Boolean(password) && rule.test(password, { email }) }));

export const isStrong = (password: string, email?: string) => checklist(password, email).every((rule) => rule.met);
